import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SEOHead from "@/components/SEOHead";
import {
  Star, BadgeCheck, MapPin, Zap, Award, Heart, ChevronRight, CalendarDays, Sparkles, Phone, Clock, MessageSquare
} from "lucide-react";

const DISCOVERY = { title: "Free 15-min Discovery Call", price: 0, duration: "15 min", discovery: true };
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import GymMap from "@/components/GymMap";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (!u ? u : (u.startsWith("http") ? u : `${BACKEND}${u}`));

function nextDates() {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push({
      key: d.toISOString().slice(0, 10),
      dayLabel: d.toLocaleDateString("en-IE", { weekday: "short" }),
      dateLabel: d.toLocaleDateString("en-IE", { day: "numeric", month: "short" }),
      dayName: DAYS[(d.getDay() + 6) % 7],
    });
  }
  return out;
}

export default function TrainerProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [service, setService] = useState(null);
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [openBook, setOpenBook] = useState(false);

  const { data: t, isLoading } = useQuery({
    queryKey: ["trainer", id],
    queryFn: async () => (await api.get(`/trainers/${id}`)).data,
  });

  const { data: favs = [] } = useQuery({
    queryKey: ["favs"],
    enabled: !!user && user.role === "customer",
    queryFn: async () => (await api.get("/favourites")).data,
  });
  const isFav = favs.some((f) => f.id === id);

  const favMut = useMutation({
    mutationFn: async () => isFav ? api.delete(`/favourites/${id}`) : api.post(`/favourites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["favs"] }),
  });

  const bookMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/bookings", {
        trainer_id: id,
        service_title: service.title,
        price: service.price,
        date: pickedDate,
        slot: pickedSlot,
        notes,
      });
      return data;
    },
    onSuccess: (b) => {
      toast.success(b.price > 0 ? "Booking requested — awaiting trainer approval" : "Discovery call booked!");
      setOpenBook(false);
      const free = b.price === 0 ? "&free=1" : "";
      nav(`/booking/success?booking_id=${b.id}${free}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Booking failed"),
  });

  if (isLoading || !t) return <div className="container-fl py-24 text-center text-neutral-500">Loading profile…</div>;

  const dates = nextDates();
  const availability = t.availability || {};
  const seoTitle = `${t.name} — Personal Trainer in ${t.city || t.county || "Ireland"}`;
  const seoDesc = t.bio
    ? t.bio.slice(0, 155)
    : `Book ${t.name}, a verified personal trainer in ${t.city || t.county || "Ireland"}. €${t.hourly_rate}/session · ${t.rating_avg?.toFixed(1) || "New"} ★`;
  const seoImage = t.photo_url ? resolveUrl(t.photo_url) : undefined;

  const startBook = (svc) => {
    if (!user) return nav("/login");
    if (user.role !== "customer") return toast.error("Log in as a customer to book");
    setService(svc);
    setPickedDate(null);
    setPickedSlot(null);
    setNotes("");
    setOpenBook(true);
  };

  return (
    <div className="container-fl py-8">
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        image={seoImage}
        url={`https://fitlink.ie/trainer/${t.slug || t.id}`}
        type="profile"
      />
      {/* HEADER */}
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div>
          <div className="relative rounded-2xl overflow-hidden bg-neutral-100 aspect-[16/10]">
            {t.photo_url ? <img src={resolveUrl(t.photo_url)} className="w-full h-full object-cover" alt={t.name}/> : null}
            <span className="absolute top-4 left-4 inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-xs font-bold">
              <BadgeCheck size={14} className="text-emerald-600"/> Verified trainer
            </span>
            {user?.role === "customer" && (
              <button data-testid="fav-toggle" onClick={() => favMut.mutate()} className="absolute top-4 right-4 rounded-full bg-white/95 p-2 hover:bg-white">
                <Heart size={18} className={isFav ? "fill-emerald-600 text-emerald-600" : "text-neutral-700"}/>
              </button>
            )}
          </div>

          <div className="mt-6 flex items-start justify-between gap-4">
            <div>
              <h1 data-testid="trainer-name" className="text-3xl sm:text-4xl font-black tracking-tighter">{t.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-neutral-600">
                <span className="inline-flex items-center gap-1"><MapPin size={14}/> {t.city}, {t.county}</span>
                <span className="inline-flex items-center gap-1"><Star size={14} className="star-on fill-emerald-600"/> <b>{t.rating_avg?.toFixed(1) || "New"}</b> · {t.rating_count || 0} reviews</span>
                <span className="inline-flex items-center gap-1"><Award size={14}/> {t.experience_years} yrs experience</span>
                {t.response_min > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <Clock size={14}/> Responds in ~{t.response_min < 60 ? `${t.response_min}m` : `${Math.round(t.response_min/60)}h`}
                  </span>
                )}
                {t.online_coaching && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Zap size={14}/> Online coaching</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(t.specialities || []).map((s) => <span key={s} className="text-xs bg-neutral-100 text-neutral-700 rounded-full px-2 py-1 font-medium">{s}</span>)}
              </div>
            </div>
          </div>

          {/* ABOUT */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-3">About</h2>
            <p className="text-neutral-700 leading-relaxed">{t.bio || "No bio yet."}</p>
          </div>

          {/* GALLERY */}
          {(t.gallery || []).length > 0 && (
            <div className="mt-10">
              <h2 className="text-2xl font-bold mb-4">Transformations & moments</h2>
              <div data-testid="trainer-gallery" className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(t.gallery || []).map((url, i) => (
                  <a key={i} href={resolveUrl(url)} target="_blank" rel="noreferrer"
                     className="aspect-square rounded-lg overflow-hidden bg-neutral-100 group">
                    <img src={resolveUrl(url)} alt={`Gallery ${i+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* SERVICES */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-4">Services & pricing</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="card-fl p-5 flex items-start justify-between gap-4 border-emerald-300 bg-emerald-50/40">
                <div>
                  <div className="font-bold flex items-center gap-2"><Phone size={14} className="text-emerald-600"/> {DISCOVERY.title}</div>
                  <div className="text-xs text-neutral-500">{DISCOVERY.duration} · no commitment</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-black text-emerald-700">FREE</div>
                  <button data-testid="book-service-discovery" onClick={() => startBook(DISCOVERY)} className="btn-accent mt-2 !py-1.5 !px-3 !text-xs">Book</button>
                </div>
              </div>
              {(t.services || []).map((s, i) => (
                <div key={i} className="card-fl p-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-bold">{s.title}</div>
                    <div className="text-xs text-neutral-500">{s.duration}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-black">€{s.price}</div>
                    <button data-testid={`book-service-${i}`} onClick={() => startBook(s)} className="btn-accent mt-2 !py-1.5 !px-3 !text-xs">Book</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* QUALIFICATIONS */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-4">Qualifications</h2>
            <ul className="space-y-2">
              {(t.qualifications || []).map((q, i) => (
                <li key={i} className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3 bg-white">
                  <Award size={16} className="text-emerald-600"/>
                  <div>
                    <div className="font-semibold text-sm">{q.title}</div>
                    <div className="text-xs text-neutral-500">{q.issuer} · {q.year}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* WEEKLY AVAILABILITY */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-4">Weekly availability</h2>
            <div className="grid grid-cols-7 gap-2 text-xs">
              {DAYS.map((d) => (
                <div key={d} className="border border-neutral-200 rounded-lg p-3 bg-white">
                  <div className="font-bold text-neutral-900 mb-2">{d}</div>
                  {(availability[d] || []).length === 0 ? (
                    <div className="text-neutral-400">—</div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {(availability[d] || []).map((s) => (
                        <span key={s} className="rounded-md bg-emerald-50 text-emerald-800 px-2 py-1 text-center font-semibold">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* GYMS + MAP */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-4">Gym locations</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {(t.gyms || []).map((g, i) => (
                <div key={i} className="card-fl p-0 overflow-hidden">
                  <GymMap lat={g.lat} lng={g.lng} name={g.name}/>
                  <div className="p-4">
                    <div className="font-bold">{g.name}</div>
                    <div className="text-sm text-neutral-500 flex items-center gap-1 mt-1"><MapPin size={12}/> {g.address}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* REVIEWS */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-4">Reviews</h2>
            {(t.reviews || []).length === 0 ? (
              <div className="text-neutral-500">No reviews yet.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {t.reviews.map((r) => (
                  <div key={r.id} className="card-fl p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({length: r.rating}).map((_, k) => <Star key={k} size={14} className="star-on fill-emerald-600"/>)}
                    </div>
                    <p className="text-neutral-700 text-sm leading-relaxed">"{r.comment}"</p>
                    <div className="mt-3 text-xs font-semibold text-neutral-500">— {r.customer_name}</div>
                    {r.response && (
                      <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Reply from {t.name}</div>
                        <p className="text-sm text-neutral-800 leading-relaxed">{r.response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* STICKY BOOKING WIDGET */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="card-fl p-6">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-3xl font-black">€{t.hourly_rate}</span>
                <span className="text-sm text-neutral-500 ml-1">/ session</span>
              </div>
              <div className="inline-flex items-center gap-1 text-sm">
                <Star size={14} className="star-on fill-emerald-600"/> <b>{t.rating_avg?.toFixed(1) || "New"}</b>
              </div>
            </div>
            <button data-testid="book-now-btn" onClick={() => startBook((t.services || [])[0] || { title: "1-to-1 Session", price: t.hourly_rate, duration: "60 min" })}
                    className="btn-accent w-full mt-5">
              <CalendarDays size={14}/> Book Now <ChevronRight size={14}/>
            </button>
            <button data-testid="book-discovery-btn" onClick={() => startBook(DISCOVERY)}
                    className="btn-secondary w-full mt-2 border-emerald-300 text-emerald-700 hover:!border-emerald-600 hover:!text-emerald-700 hover:bg-emerald-50">
              <Phone size={14}/> Book a FREE 15-min discovery call
            </button>
            <button data-testid="ask-question-btn" onClick={() => {
                if (!user) return nav("/login");
                nav(`/messages?with=${t.user_id || t.id}`);
              }}
              className="btn-secondary w-full mt-2">
              <MessageSquare size={14}/> Ask a question first
            </button>
            <p className="text-xs text-neutral-500 mt-2 leading-snug">Not sure yet? Chat with {t.name.split(" ")[0]} first — no commitment, no card required.</p>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-neutral-500">
              <div className="flex items-center gap-1"><BadgeCheck size={12} className="text-emerald-600"/> Verified</div>
              <div className="flex items-center gap-1"><Sparkles size={12} className="text-emerald-600"/> Secure payment</div>
            </div>
          </div>
        </aside>
      </div>

      {/* BOOKING DIALOG */}
      <Dialog open={openBook} onOpenChange={setOpenBook}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Book: {service?.title} — €{service?.price}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Pick a day</div>
              <div className="grid grid-cols-7 gap-1">
                {dates.map((d) => {
                  const has = (availability[d.dayName] || []).length > 0;
                  return (
                    <button key={d.key} data-testid={`book-date-${d.key}`}
                            disabled={!has}
                            onClick={() => { setPickedDate(d.key); setPickedSlot(null); }}
                            className={`text-xs p-2 rounded-md border ${pickedDate === d.key ? "bg-neutral-900 text-white border-neutral-900" : has ? "bg-white border-neutral-200 hover:border-neutral-900" : "bg-neutral-50 text-neutral-300 border-neutral-100 cursor-not-allowed"}`}>
                      <div className="font-bold">{d.dayLabel}</div>
                      <div>{d.dateLabel}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            {pickedDate && (
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Pick a time</div>
                <div className="flex flex-wrap gap-2">
                  {(availability[dates.find(x => x.key === pickedDate)?.dayName] || []).map((s) => (
                    <button key={s} data-testid={`book-slot-${s}`} onClick={() => setPickedSlot(s)}
                            className={`pill ${pickedSlot === s ? "pill-active" : ""}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Notes (optional)</div>
              <textarea data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                        className="w-full rounded-md border border-neutral-200 p-3 text-sm outline-none focus:border-neutral-900"/>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="book-confirm-btn" disabled={!pickedDate || !pickedSlot || bookMut.isPending}
                    onClick={() => bookMut.mutate()}
                    className="btn-accent w-full">
              {bookMut.isPending
                ? "Booking…"
                : service?.discovery
                  ? "Confirm free discovery call"
                  : `Request booking (€${service?.price})`}
            </button>
            {!service?.discovery && (
              <p className="text-xs text-neutral-500 text-center mt-2 w-full">
                No charge yet — you'll pay securely once the trainer accepts.
              </p>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
